import { createFileRoute, Link } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { api } from '../lib/api.js'
import { useAuth } from '../lib/auth.js'
import { PageHeader } from '../components/PageHeader.js'
import { Card, CardBody } from '../components/Card.js'

export const Route = createFileRoute('/_app/my-classes')({
  component: MyClassesPage,
})

interface ClassRow {
  id: string
  academyId: string
  name: string
  academicYear: string | null
  isActive: boolean
}

interface TeachersPayload {
  userId: string
  classes: { id: string; name: string }[]
}

function MyClassesPage() {
  const { user } = useAuth()
  const userId = user?.id

  // Pull teacher's own assignments via /teachers (admin-only normally; teachers
  // can also read their own row). Falling back to filtering /classes if the
  // teachers route blocks them.
  const teachersQuery = useQuery({
    queryKey: ['teachers'],
    enabled: false, // teachers don't have access to /teachers
    queryFn: async () => {
      const res = await api<TeachersPayload[]>('/api/v1/teachers')
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
  })

  // Teachers see classes via the /classes route (RLS lets them read same-academy).
  const classes = useQuery({
    queryKey: ['classes'],
    queryFn: async () => {
      const res = await api<ClassRow[]>('/api/v1/classes')
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
  })

  // For now show all classes the teacher's academy has — teacher_classes RLS
  // means they could only mark attendance/tests for the assigned subset, but
  // the list above includes everything. A future server endpoint should expose
  // /me/classes; using the full list is acceptable in dev.
  const myClasses = classes.data ?? []
  void teachersQuery
  void userId

  return (
    <div className="space-y-8">
      <PageHeader
        title="My classes"
        description="Mark daily attendance, create tests, and enter scores for your assigned classes."
      />

      {classes.isLoading && <p className="text-sm text-fg-subtle">Loading…</p>}
      {classes.error && (
        <p className="text-sm text-danger">
          {classes.error instanceof Error ? classes.error.message : 'Failed to load'}
        </p>
      )}

      {classes.data && myClasses.length === 0 && (
        <Card>
          <CardBody className="text-center py-10 text-sm text-fg-muted">
            No classes assigned yet. Ask your academy admin to assign you a class.
          </CardBody>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {myClasses
          .filter((c) => c.isActive)
          .map((c) => (
            <Card key={c.id}>
              <CardBody>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-base font-semibold text-fg">{c.name}</p>
                    <p className="text-xs text-fg-subtle mt-0.5">
                      {c.academicYear ?? 'Academic year not set'}
                    </p>
                  </div>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  <Link
                    to="/attendance/$classId"
                    params={{ classId: c.id }}
                    className="rounded-md border border-border px-3 py-1.5 text-xs font-medium text-fg hover:bg-surface-muted"
                  >
                    Mark attendance →
                  </Link>
                  <Link
                    to="/tests"
                    search={{ classId: c.id }}
                    className="rounded-md border border-border px-3 py-1.5 text-xs font-medium text-fg hover:bg-surface-muted"
                  >
                    Tests →
                  </Link>
                </div>
              </CardBody>
            </Card>
          ))}
      </div>
    </div>
  )
}
